import { pathLabelOf, type Container } from "@egalregal/shared";
import type { ScanHistoryEntry } from "../../shared/store/scanHistory";
import { getMirroredPlacements } from "../search/catalogStore";
import { createResolveDeps, resolveScan, type ResolveDeps } from "./resolution";

/**
 * Where a history entry points right now. The stored outcome is only a display
 * snapshot; opening an entry re-resolves live so renames, trash and deletion
 * show current truth (ADR-0041).
 */
export type ScanHistoryTarget =
  | { kind: "container"; id: string; label: string; path: string; trashed: boolean }
  | { kind: "item"; id: string; label: string; path?: string }
  | { kind: "payload" }
  /**
   * The code resolved to nothing the browser can reach: it is not in the read
   * cache and the server could not answer. Reported as offline, never as an
   * unknown/deleted target.
   */
  | { kind: "offline"; code: string }
  | { kind: "none" };

/** The live catalog slices a container target is resolved from. */
export interface ScanHistoryTargetScope {
  containers: readonly Container[];
  trash: readonly Container[];
}

/**
 * Resolve a history entry's current target through the same cache-first
 * resolver the scanner uses (scan-resolution 03): live container → trash →
 * mirrored item → cached label → network. A cached container answers offline
 * and opens; an uncached code on an unreachable server reports `offline` rather
 * than pretending the target no longer exists.
 *
 * Each catalog target carries its display path, so the overlay can show where
 * it is instead of a generic kind.
 *
 * @param entry the stored scan-history entry.
 * @param scope the app store's readable containers and trash, for paths.
 * @param deps the resolver lookups (production defaults; tests inject fakes).
 * @returns the live target, or `offline`/`none` when it cannot resolve.
 */
export async function resolveScanHistoryTarget(
  entry: ScanHistoryEntry,
  scope: ScanHistoryTargetScope,
  deps: ResolveDeps = createResolveDeps({
    containers: () => scope.containers,
    trash: () => scope.trash,
  }),
): Promise<ScanHistoryTarget> {
  const result = await resolveScan(entry.payload, deps);
  switch (result.kind) {
    case "payload":
      return { kind: "payload" };
    case "container":
      return containerTarget(result.container, false, scope);
    case "trash":
      return containerTarget(result.container, true, scope);
    case "item":
      return {
        kind: "item",
        id: result.item.id,
        label: result.item.name,
        path: itemPath(result.item.id, scope.containers),
      };
    case "offline-uncached":
      return { kind: "offline", code: result.code };
    case "product":
      // A provider preview has no catalog target; an offline lookup is the
      // offline case the overlay must name.
      return result.reason === "offline"
        ? { kind: "offline", code: result.code }
        : { kind: "none" };
    case "unknown-label":
      return { kind: "none" };
    default:
      return assertNever(result);
  }
}

/**
 * A container target with its breadcrumb. A container outside the live slices
 * (read cache or network) contributes itself, so its path falls back to its
 * own name instead of an empty string.
 */
function containerTarget(
  container: Container,
  trashed: boolean,
  scope: ScanHistoryTargetScope,
): ScanHistoryTarget {
  // Trash is a subset of the same tree, so the combined pool keeps a trashed
  // container's ancestors resolvable for its breadcrumb.
  const all = [...scope.containers, ...scope.trash, container];
  const path = pathLabelOf(all, container.id) || container.name;
  return { kind: "container", id: container.id, label: container.name, path, trashed };
}

/** The best (lexicographically first) readable container path of an item. */
function itemPath(itemId: string, containers: readonly Container[]): string | undefined {
  const paths = getMirroredPlacements()
    .filter((placement) => placement.itemId === itemId)
    .map((placement) => {
      const container = containers.find((candidate) => candidate.id === placement.containerId);
      return container ? pathLabelOf(containers, container.id) : undefined;
    })
    .filter((path): path is string => Boolean(path))
    .sort((left, right) => left.localeCompare(right));
  return paths[0];
}

/** Compile-time exhaustiveness check for the resolution union. */
function assertNever(value: never): never {
  throw new Error(`Unhandled scan resolution: ${JSON.stringify(value)}`);
}
